import { differenceInDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { TIMELINE_ROW_AXIS_LABEL_WIDTH } from './layout/TimelineRowAxisLabel';

export const DEFAULT_ROW_HEADER_WIDTH = 120;
export const VERTICAL_ROW_HEADER_WIDTH = 32;
export const PARENT_HEADER_HEIGHT = 32;
export const TIMELINE_HEADER_HEIGHT = 70;

export const RESIZE_HANDLE_WIDTH = 4;
export const RESIZE_HANDLE_HITBOX_PADDING = 8;
export const RESIZE_HANDLE_CENTER_OFFSET = 2;

// When a minDayWidth is set (gantt), fitted content fills to just under the
// container width instead of exactly to the edge. Exact fill sits on the
// horizontal-scrollbar threshold, so a resize or rounding difference can toggle
// the scrollbar → change the measured width → refit → flicker loop. A few px of
// slack keeps it stable. Both the day-width and the fit-factor hooks use this so
// the rendered fill and the computed fit floor agree.
const FIT_SCROLLBAR_BUFFER = 2;

const useObservedContainerWidth = (
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  isExportMode: boolean,
): number => {
  const [containerWidth, setContainerWidth] = useState(() =>
    isExportMode ? window.innerWidth : 0,
  );

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.target.clientWidth);
      }
    });

    resizeObserver.observe(element);
    setContainerWidth(element.clientWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  return containerWidth;
};

/**
 * The lowest zoom factor at which the timeline columns still fill (or overflow)
 * the container — i.e. zooming out further than this would leave empty space to
 * the right. 1 when the content already fits at 1x (short ranges); below 1 only
 * when the range is long enough that `fittedDayWidth` is pinned to `minDayWidth`
 * and the content overflows. Derived from the observed container width and date
 * range only (never the post-zoom rendered width), so it is stable across zoom
 * changes and cannot feed a zoom/resize loop.
 */
export const useTimelineFitZoomFactor = ({
  scrollContainerRef,
  from,
  to,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
  minDayWidth,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  from: string;
  to: string;
  rowHeaderWidth?: number;
  minDayWidth: number;
}): number => {
  const containerWidth = useObservedContainerWidth(scrollContainerRef, false);

  return useMemo(() => {
    const timelineWidth = containerWidth - rowHeaderWidth - FIT_SCROLLBAR_BUFFER;
    const totalDays = differenceInDays(new Date(to), new Date(from)) + 1;
    if (timelineWidth <= 0 || totalDays <= 0) return 1;
    const unflooredFitDayWidth = timelineWidth / totalDays;
    // fittedDayWidth is max(unfloored, minDayWidth). It overflows the container
    // only when pinned to the floor, and then the fit factor is unfloored/floor.
    return Math.min(1, unflooredFitDayWidth / minDayWidth);
  }, [containerWidth, from, to, rowHeaderWidth, minDayWidth]);
};

export const useTimelineColumnDayWidth = ({
  scrollContainerRef,
  hasVerticalSubHeaders,
  hasRowAxisLabel = true,
  from,
  to,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
  isExportMode = false,
  minDayWidth,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  hasVerticalSubHeaders: boolean;
  hasRowAxisLabel?: boolean;
  from: string;
  to: string;
  rowHeaderWidth?: number;
  isExportMode?: boolean;
  minDayWidth?: number;
}) => {
  const containerWidth = useObservedContainerWidth(scrollContainerRef, isExportMode);

  return useMemo(() => {
    const axisLabelWidth = hasRowAxisLabel ? TIMELINE_ROW_AXIS_LABEL_WIDTH : 0;
    const timelineWidth = hasVerticalSubHeaders
      ? containerWidth - rowHeaderWidth - PARENT_HEADER_HEIGHT - axisLabelWidth
      : containerWidth - rowHeaderWidth - axisLabelWidth;
    // See FIT_SCROLLBAR_BUFFER: a few px of slack for the gantt (minDayWidth) case
    // so content fills to just under the edge and can't thrash the scrollbar on
    // resize. The larger buffer for the no-minDayWidth (forced-fit) case is kept.
    const scrollbarBuffer = minDayWidth !== undefined ? FIT_SCROLLBAR_BUFFER : 10;
    const bufferedWidth = timelineWidth - scrollbarBuffer;
    const totalDays = differenceInDays(new Date(to), new Date(from)) + 1;
    const fittedWidth =
      totalDays <= 31 ? Math.floor(bufferedWidth / totalDays) : bufferedWidth / totalDays;

    if (minDayWidth !== undefined) {
      return Math.max(fittedWidth, minDayWidth);
    }
    return fittedWidth;
  }, [
    containerWidth,
    from,
    to,
    hasVerticalSubHeaders,
    hasRowAxisLabel,
    rowHeaderWidth,
    minDayWidth,
  ]);
};
